import Tool, { type ToolArgs } from '../../../tools/Tool';
import React, { useState, useRef } from 'react';
import { Transforms, Editor } from 'slate';
import { ReactEditor } from 'slate-react';
import styles from '../../../components/Toolbar/Toolbar.module.css';
import classNames from 'classnames';
import EmbedCommon from '../EmbedCommon';
import { isEmbedElement, isArtifactEmbedElement } from '../../../types/guards';
import useFocusElementToolbarInput from '../../../components/ElementToolbar/useFocusElementToolbarInput';

const EditSrcModal = ({ editor, element, toolbarHideModal }: ToolArgs) => {
  const defaultValue =
    element && isEmbedElement(element) ? element.embed_src ?? '' : '';
  const [originalValue] = useState(defaultValue);
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useFocusElementToolbarInput(inputRef);

  if (!element) return <></>;

  const path = ReactEditor.findPath(editor, element);
  const updateLinkElement = (url: string) => {
    Transforms.setNodes(
      editor,
      { embed_src: url },
      {
        at: path,
      }
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
      case 'Tab':
        toolbarHideModal?.();
        event.preventDefault();
        updateLinkElement(value);
        break;
      case 'Escape':
        toolbarHideModal?.();
        event.preventDefault();
        updateLinkElement(originalValue);
        break;
    }
  };

  const handleBlur = () => {
    updateLinkElement(value);
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="text"
        className={classNames('unstyled-input', styles.toolbarInput)}
        placeholder="URL"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />
    </div>
  );
};

// Edit the embed src attribute
class ToolEditSrc extends Tool {
  static toolName = 'Edit source';
  static iconClass = 'fa-regular fa-link';
  static withoutRefocus = true;

  // Show the modal view to editor the URL.
  static action({ editor, element, toolbarShowModal }: ToolArgs): void {
    toolbarShowModal?.(ToolEditSrc);
  }

  static available({ editor }: ToolArgs): boolean {
    const embeds = Editor.above(editor, {
      mode: 'lowest',
      match: isArtifactEmbedElement,
    });

    if (embeds && embeds.length > 0) {
      return false;
    }

    return true;
  }

  static renderToolbarButtonIcon({ editor, element }: ToolArgs): JSX.Element {
    const src = element && isEmbedElement(element) ? element.embed_src : '';
    const embedType = EmbedCommon.embedTypeForUrl(src);
    let prompt = 'Source';
    if (embedType) {
      prompt = embedType.title;
    }

    return (
      <span
        style={{
          maxWidth: 200,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        className={styles.toolbarButtonText}
      >
        {prompt}
      </span>
    );
  }

  static renderToolbarModal(toolArgs: ToolArgs): JSX.Element {
    return EditSrcModal(toolArgs);
  }
}

export default ToolEditSrc;
